import { Alert, Button, Checkbox, CircularProgress, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PinInput from 'src/components/signature/pin-input';
import SetupSigningWizard from 'src/components/signature/setup-signing-wizard';
import SignatureRender from 'src/components/signature/signature-render';
import { useSignatureValidation } from 'src/hooks/use-signature-validation';
import { applyBulkSignatureRequest } from 'src/redux/actions/therapist-dashboard-action';

interface SignatureModalProps {
  onClose: () => void;
  signDocument: any;
  coSignDocument: any;
  //  THIN-7681: PIN-challenge error surfaced by the parent (wrong PIN / no PIN set); the
  //  parent keeps the modal open so the user can retry.
  pinError?: string | null;
}

const SignVector = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M12.9264 3.05139L14.3326 1.64515C14.9428 1.03495 15.9322 1.03495 16.5424 1.64515C17.1526 2.25534 17.1526 3.24466 16.5424 3.85485L7.69349 12.7037C7.25292 13.1443 6.70953 13.4681 6.11241 13.646L3.875 14.3125L4.54148 12.0751C4.71935 11.478 5.04321 10.9346 5.48377 10.494L12.9264 3.05139ZM12.9264 3.05139L15.125 5.24999M13.875 10.9792V14.9375C13.875 15.973 13.0355 16.8125 12 16.8125H3.25C2.21447 16.8125 1.375 15.973 1.375 14.9375V6.18749C1.375 5.15196 2.21447 4.31249 3.25 4.31249H7.20833"
        stroke="white"
        strokeWidth="1.3125"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const MySignatureModal: FC<SignatureModalProps> = ({ onClose, signDocument, coSignDocument, pinError }) => {
  const dispatch = useDispatch();

  const therapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );

  const { enabled: signatureValidationEnabled, hasPin, statusLoading } = useSignatureValidation();
  const fullName = [therapistProfile?.lastName, therapistProfile?.firstName]
    .filter(Boolean)
    .join(' ');
  const showSetup = signatureValidationEnabled && !hasPin && !statusLoading;

  const isBulkSignatureSigned = useSelector((state: any) => state.therapistBulkSignatureReducer);
  const [signatureName, setSignatureName] = useState('');
  const [pin, setPin] = useState('');
  const [agree, setAgree] = useState(false);

  const docCount = (signDocument?.length || 0) + (coSignDocument?.length || 0);

  //  Clear the PIN whenever the parent reports a challenge failure so the user re-enters it.
  useEffect(() => {
    if (pinError) setPin('');
  }, [pinError]);

  const handleSign = () => {
    const getSignatureDoc = signDocument?.map((res: any) => ({
      formSubmissionId: res?.uuid,
      documentTypes: res?.formType,
      isSupervisor: false,
    }));
    const getCoSignatureDoc = coSignDocument?.map((res: any) => ({
      formSubmissionId: res?.uuid,
      documentTypes: res?.formType,
      isSupervisor: true,
    }));
    const body = [...getSignatureDoc, ...getCoSignatureDoc];

    if (signatureValidationEnabled) {
      if (pin.length !== 4 || !agree) return;
      dispatch(
        applyBulkSignatureRequest({
          body,
          params: { signerName: fullName, consentConfirmation: agree, pin },
        })
      );
      return;
    }

    //  Legacy free-text path (flag off) — unchanged.
    if (!signatureName.trim()) return;
    dispatch(
      applyBulkSignatureRequest({
        body,
        params: { signerName: signatureName, consentConfirmation: agree },
      })
    );
    setSignatureName('');
  };

  const handleClose = () => {
    setSignatureName('');
    setPin('');
    onClose();
  };

  const handleChangesStatus = (e: any) => {
    setAgree(e?.target?.checked);
  };

  const signDisabled = signatureValidationEnabled
    ? pin.length !== 4 || !agree
    : !signatureName.trim() || !agree;

  return (
    <>
      {!showSetup && (
        <DialogTitle>{signatureValidationEnabled ? `Sign ${docCount} document${docCount === 1 ? '' : 's'}` : 'Add Signature'}</DialogTitle>
      )}
      <DialogContent>
        {showSetup ? (
          <SetupSigningWizard onCancel={handleClose} />
        ) : (
          <>
        <Box sx={{ mb: '20px' }}>
          <Stack display={'flex'} flexDirection={'row'} gap={1}>
            <Typography variant="subtitle1">Name:</Typography>
            <Typography variant="body2">
              {therapistProfile?.lastName + ' ' + therapistProfile?.firstName}
            </Typography>
          </Stack>
          <Stack display={'flex'} flexDirection={'row'} gap={1}>
            <Typography variant="subtitle1">Credentials:</Typography>
            <Typography variant="body2">{therapistProfile?.credentials}</Typography>
          </Stack>
          <Stack display={'flex'} flexDirection={'row'} gap={1}>
            <Typography variant="subtitle1">License #:</Typography>
            <Typography variant="body2">{therapistProfile?.licenseNumber}</Typography>
          </Stack>
        </Box>

        {signatureValidationEnabled ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Your signature
            </Typography>
            {/* APIT-655: the preview has to show what the PDF will print. */}
            <SignatureRender
              name={fullName}
              license={therapistProfile?.licenseNumber}
              credentials={therapistProfile?.credentials}
              signatureLineOverride={therapistProfile?.signatureLineOverride}
              credentialsLineOverride={therapistProfile?.credentialsLineOverride}
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Enter your 4-digit signing PIN
              </Typography>
              <PinInput value={pin} onChange={setPin} error={!!pinError} mask autoFocus />
              {pinError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {pinError}
                </Typography>
              )}
            </Box>
            <Alert severity="info" sx={{ mt: 2, py: 0.25 }}>
              One PIN signs all selected documents. Each still gets its own signature.
            </Alert>
          </Box>
        ) : (
          <TextField
            fullWidth
            label="Type your signature"
            placeholder="Type your signature"
            value={signatureName}
            type="text"
            onChange={(e) => setSignatureName(e.target.value)}
            sx={{ mb: 2, '& input': { fontSize: 'larger', fontStyle: 'oblique' } }}
          />
        )}

        <Stack display={'flex'} flexDirection={'row'} alignItems={'flex-start'} width={'420px'}>
          <Checkbox checked={agree} onChange={handleChangesStatus} />
          <Typography variant="body2" flexWrap={'wrap'}>
            I agree that my electronic signature is legally binding and holds the same validity as my
            handwritten signature, in accordance with applicable laws and regulations.
          </Typography>
        </Stack>
          </>
        )}
      </DialogContent>
      {!showSetup && (
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSign}
          disabled={signDisabled}
          startIcon={isBulkSignatureSigned?.loading ? <CircularProgress size={20} /> : <SignVector />}
        >
          {isBulkSignatureSigned?.loading
            ? 'Signing...'
            : signatureValidationEnabled
            ? `Sign ${docCount} document${docCount === 1 ? '' : 's'}`
            : 'Sign'}
        </Button>
      </DialogActions>
      )}
    </>
  );
};

export default MySignatureModal;
